/**
 * EmployeePortalLayout — shared sidebar + top-bar layout for all employee-facing pages.
 *
 * Structure:
 *   ┌──────────┬────────────────────────────────────────┐
 *   │          │  Top Bar (user info, settings, logout) │
 *   │ Sidebar  ├────────────────────────────────────────┤
 *   │ (nav)    │  Page content (children)               │
 *   └──────────┴────────────────────────────────────────┘
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  LayoutDashboard,
  CalendarDays,
  User,
  LogOut,
  ChevronLeft,
  Menu,
  X,
  FileText,
  Wallet,
  Receipt,
  ClipboardEdit,
  PiggyBank,
  CheckSquare,
  Briefcase,
} from 'lucide-react';
import RoleSwitcher from './shared/RoleSwitcher';
import NotificationCenter from './NotificationCenter';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Nav items are built inside the component so labels update when language changes
const NAV_ITEM_DEFS = [
  { icon: LayoutDashboard, key: 'dashboard',        path: '/employee-portal' },
  { icon: CalendarDays,    key: 'myLeave',           path: '/my-leave' },
  { icon: ClipboardEdit,   key: 'swipeCorrections',  path: '/my-swipe-requests' },
  { icon: Briefcase,       key: 'odRequests',         path: '/my-od-requests' },
  { icon: Wallet,          key: 'myPayslips',         path: '/my-payslips' },
  { icon: PiggyBank,       key: 'myLoans',            path: '/my-loans' },
  { icon: Receipt,         key: 'reimbursements',     path: '/my-reimbursements' },
  { icon: FileText,        key: 'myDocuments',        path: '/my-documents' },
  { icon: CheckSquare,     key: 'leaveApprovals',     path: '/my-leave-approvals' },
  { icon: User,            key: 'myProfile',          path: '/profile' },
];

const PAGE_TITLE_KEYS = {
  '/employee-portal':      'dashboard',
  '/my-leave':             'myLeave',
  '/my-swipe-requests':    'swipeCorrections',
  '/my-od-requests':       'odRequests',
  '/my-payslips':          'myPayslips',
  '/my-loans':             'myLoans',
  '/my-reimbursements':    'reimbursements',
  '/my-documents':         'myDocuments',
  '/my-leave-approvals':   'leaveApprovals',
  '/profile':              'myProfile',
};

const EmployeePortalLayout = ({ children }) => {
  const { isDark, theme } = useTheme();
  const { currentUser, logout } = useAuth();
  const { t } = useTranslation();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // When the user is device-control-only, restrict to the dashboard route only.
  // Any attempt to visit another employee-portal page is redirected back.
  const isDeviceOnly = !!currentUser?.device_control_only;
  useEffect(() => {
    if (isDeviceOnly && location.pathname !== '/employee-portal') {
      navigate('/employee-portal', { replace: true });
    }
  }, [isDeviceOnly, location.pathname, navigate]);

  // "Approvals" nav item (covers both Leave and Swipe Correction approvals on
  // one page) is shown only to employees who are configured as a leave
  // approver for someone (any level) — regardless of admin/manager portal
  // access. Swipe manager routing normally derives from the same approver
  // chain, so this single check covers both. Checked on mount; result is
  // cached in sessionStorage (keyed per-user) so on subsequent loads within
  // the same session the item renders immediately — same as every other
  // static nav item — instead of popping in after the API call resolves. The
  // API call still always runs in the background to keep the cache correct
  // (e.g. if approver status changes).
  const approverCacheKey = `ep_isApprover_${currentUser?.user_id || currentUser?.email || 'anon'}`;
  const [isApprover, setIsApprover] = useState(() => sessionStorage.getItem(approverCacheKey) === '1');
  useEffect(() => {
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/attendance/india/leave/my-approvees`, { withCredentials: true })
      .then((res) => {
        const result = (res.data?.data || []).length > 0;
        if (!cancelled) setIsApprover(result);
        sessionStorage.setItem(approverCacheKey, result ? '1' : '0');
      })
      .catch(() => { if (!cancelled) setIsApprover(false); });
    return () => { cancelled = true; };
  }, [approverCacheKey]);

  // Close mobile drawer on navigation
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLogout = async () => { await logout(); navigate('/signin'); };

  const name     = currentUser?.full_name || currentUser?.first_name || currentUser?.email || 'Employee';
  const email    = currentUser?.email || '';
  const initials = name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();

  // Build nav items inside the render so labels re-evaluate when language changes.
  // Device-control-only employees see only the main dashboard item.
  const navItems = NAV_ITEM_DEFS
    .filter(({ key }) => !isDeviceOnly || key === 'dashboard')
    .filter(({ key }) => key !== 'leaveApprovals' || isApprover)
    .map(({ icon, key, path }) => ({
      icon, path,
      label: t(`employeePortal.nav.${key}`),
    }));

  const pageTitleKey = PAGE_TITLE_KEYS[location.pathname];
  const pageTitle = pageTitleKey
    ? t(`employeePortal.pageTitles.${pageTitleKey}`)
    : t('employeePortal.pageTitles.default');

  // Theme tokens — fully theme-aware so same-mode switches are visible
  const sidebarBg  = theme.sidebarBg;
  const topBarBg   = theme.headerBg;
  const divider    = isDark ? 'border-slate-700' : 'border-white/30';
  const textMuted  = isDark ? 'text-gray-400' : 'text-gray-500';
  const hoverItem  = isDark
    ? 'text-gray-300 hover:bg-slate-700/50 hover:text-white'
    : 'text-gray-600 hover:bg-black/5 hover:text-gray-900';

  // Active nav item colour follows the theme's primary accent
  const activePrimaryMap = {
    blue:   isDark ? 'bg-blue-600/30 text-blue-300'    : 'bg-blue-50 text-blue-700',
    cyan:   isDark ? 'bg-cyan-600/30 text-cyan-300'    : 'bg-cyan-50 text-cyan-700',
    green:  isDark ? 'bg-green-600/30 text-green-300'  : 'bg-green-50 text-green-700',
    purple: isDark ? 'bg-purple-600/30 text-purple-300': 'bg-purple-50 text-purple-700',
    orange: isDark ? 'bg-orange-600/30 text-orange-300': 'bg-orange-50 text-orange-700',
    rose:   isDark ? 'bg-rose-600/30 text-rose-300'    : 'bg-rose-50 text-rose-700',
    gray:   isDark ? 'bg-gray-600/30 text-gray-300'    : 'bg-gray-100 text-gray-700',
    indigo: isDark ? 'bg-indigo-600/30 text-indigo-300': 'bg-indigo-50 text-indigo-700',
  };
  const activeItem = `${activePrimaryMap[theme.primary] || activePrimaryMap.blue} font-semibold`;

  // Shared nav content to reuse in both desktop sidebar and mobile drawer
  const SidebarNav = ({ onNavigate }) => (
    <>
      {/* Brand */}
      <div className={`flex items-center gap-2.5 px-3 py-[18px] border-b ${divider} ${!collapsed || onNavigate ? '' : 'justify-center'}`}>
        <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-blue-500 rounded-lg flex items-center justify-center shrink-0">
          <span className="text-white font-bold text-sm">V</span>
        </div>
        {(!collapsed || onNavigate) && (
          <p className={`text-sm font-bold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Vivya<span className="text-blue-500">Watch</span>
          </p>
        )}
      </div>

      {/* Nav items */}
      <nav className="flex-1 px-2 py-4 space-y-0.5 overflow-y-auto">
        {navItems.map(({ icon: Icon, label, path }) => {
          const isActive = location.pathname === path;
          return (
            <button
              key={path}
              onClick={() => { navigate(path); onNavigate?.(); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive ? activeItem : hoverItem
              } ${collapsed && !onNavigate ? 'justify-center' : ''}`}
              title={collapsed && !onNavigate ? label : undefined}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon className="w-5 h-5 shrink-0" />
              {(!collapsed || onNavigate) && <span className="truncate">{label}</span>}
            </button>
          );
        })}
      </nav>

      {/* Bottom: logout */}
      <div className={`border-t px-2 py-3 ${divider}`}>
        <button
          onClick={handleLogout}
          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            isDark ? 'text-gray-400 hover:bg-slate-700 hover:text-red-400' : 'text-gray-500 hover:bg-red-50 hover:text-red-600'
          } ${collapsed && !onNavigate ? 'justify-center' : ''}`}
          title={collapsed && !onNavigate ? t('employeePortal.nav.logout') : undefined}
          aria-label={t('employeePortal.nav.logout')}
        >
          <LogOut className="w-4 h-4 shrink-0" />
          {(!collapsed || onNavigate) && <span>{t('employeePortal.nav.logout')}</span>}
        </button>
      </div>
    </>
  );

  return (
    <div className={`flex min-h-screen bg-gradient-to-br ${theme.background}`}>

      {/* ── Mobile overlay drawer ── */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden" aria-modal="true" role="dialog">
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileOpen(false)} aria-hidden="true" />
          {/* Drawer panel */}
          <div className={`fixed left-0 top-0 h-full w-64 flex flex-col shadow-2xl z-50 ${sidebarBg}`}>
            <div className="flex items-center justify-between px-3 py-[14px]">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-blue-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">V</span>
              </div>
              <button onClick={() => setMobileOpen(false)}
                className={`p-1.5 rounded-md ${isDark ? 'hover:bg-slate-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                aria-label="Close menu">
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
              {navItems.map(({ icon: Icon, label, path }) => {
                const isActive = location.pathname === path;
                return (
                  <button
                    key={path}
                    onClick={() => navigate(path)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                      isActive ? activeItem : hoverItem
                    }`}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <Icon className="w-5 h-5 shrink-0" />
                    <span className="truncate">{label}</span>
                  </button>
                );
              })}
            </nav>
            <div className={`border-t px-2 py-3 ${divider}`}>
              <button onClick={handleLogout}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isDark ? 'text-gray-400 hover:bg-slate-700 hover:text-red-400' : 'text-gray-500 hover:bg-red-50 hover:text-red-600'
                }`}>
                <LogOut className="w-4 h-4 shrink-0" />
                <span>{t('employeePortal.nav.logout')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Desktop Sidebar (hidden on mobile) ── */}
      <aside
        className={`hidden md:flex flex-col flex-shrink-0 transition-all duration-300 border-r ${isDark ? 'border-slate-700' : 'border-white/30'} ${sidebarBg} ${collapsed ? 'w-16' : 'w-56'}`}
        style={{ position: 'sticky', top: 0, height: '100vh' }}
      >
        {/* Brand */}
        <div className={`flex items-center gap-2.5 px-3 py-[18px] border-b ${divider} ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-blue-500 rounded-lg flex items-center justify-center shrink-0">
            <span className="text-white font-bold text-sm">V</span>
          </div>
          {!collapsed && (
            <p className={`text-sm font-bold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Vivya<span className="text-blue-500">Watch</span>
            </p>
          )}
        </div>

        {/* Nav items */}
        <nav className="flex-1 px-2 py-4 space-y-0.5 overflow-y-auto">
          {navItems.map(({ icon: Icon, label, path }) => {
            const isActive = location.pathname === path;
            return (
              <button
                key={path}
                onClick={() => navigate(path)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive ? activeItem : hoverItem
                } ${collapsed ? 'justify-center' : ''}`}
                title={collapsed ? label : undefined}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon className="w-5 h-5 shrink-0" />
                {!collapsed && <span className="truncate">{label}</span>}
              </button>
            );
          })}
        </nav>

        {/* Bottom: logout only */}
        <div className={`border-t px-2 py-3 ${divider}`}>
          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isDark ? 'text-gray-400 hover:bg-slate-700 hover:text-red-400' : 'text-gray-500 hover:bg-red-50 hover:text-red-600'
            } ${collapsed ? 'justify-center' : ''}`}
            title={collapsed ? t('employeePortal.nav.logout') : undefined}
            aria-label={t('employeePortal.nav.logout')}
          >
            <LogOut className="w-4 h-4 shrink-0" />
            {!collapsed && <span>{t('employeePortal.nav.logout')}</span>}
          </button>
        </div>
      </aside>

      {/* ── Right side: Top Bar + Content ── */}
      <div className="flex-1 min-w-0 flex flex-col">

        {/* Top Bar */}
        <header className={`sticky top-0 z-20 flex items-center justify-between px-5 py-3 border-b ${isDark ? 'border-slate-700' : 'border-white/30'} ${topBarBg}`}>
          {/* Left: mobile hamburger (md:hidden) + desktop collapse toggle + page title */}
          <div className="flex items-center gap-3">
            {/* Mobile hamburger — only visible on small screens */}
            <button
              onClick={() => setMobileOpen(true)}
              className={`md:hidden p-1.5 rounded-md transition-colors ${isDark ? 'hover:bg-slate-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-900'}`}
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            {/* Desktop sidebar collapse toggle */}
            <button
              onClick={() => setCollapsed(!collapsed)}
              className={`hidden md:block p-1.5 rounded-md transition-colors ${isDark ? 'hover:bg-slate-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-900'}`}
              aria-label={collapsed ? t('employeePortal.nav.expandSidebar') : t('employeePortal.nav.collapseSidebar')}
            >
              {collapsed ? <Menu className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
            </button>
            <h1 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{pageTitle}</h1>
          </div>

          {/* Right: role switcher, settings, bell, user chip */}
          <div className="flex items-center gap-2">
            <RoleSwitcher variant="default" showLabel={true} />

            {/* Notification dropdown — click any notification to navigate to its page */}
            <NotificationCenter />

            {/* User chip */}
            <div className={`flex items-center gap-2.5 pl-2 pr-3 py-1.5 rounded-full cursor-pointer transition-colors ${isDark ? 'bg-slate-700/60 hover:bg-slate-700' : 'bg-gray-100 hover:bg-gray-200'}`}
              onClick={() => navigate('/profile')}
              role="button"
              tabIndex={0}
              aria-label="My profile"
            >
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-blue-400 flex items-center justify-center shrink-0">
                <span className="text-white text-xs font-bold">{initials}</span>
              </div>
              <div className="hidden sm:block min-w-0">
                <p className={`text-xs font-semibold leading-tight truncate max-w-[120px] ${isDark ? 'text-white' : 'text-gray-900'}`}>{name}</p>
                <p className={`text-xs leading-tight truncate max-w-[120px] ${textMuted}`}>{email}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default EmployeePortalLayout;
